import React, { HTMLAttributes, useEffect } from 'react'
import { Script } from 'gatsby'
import * as CookieConsent from 'vanilla-cookieconsent'
import Cookies from 'universal-cookie'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import '../styles/_cc.scss'

import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { ThemeProvider } from '../contexts/ThemeContext'
import { ThemeWrapper } from '../components/ThemeWrapper'

import * as Sentry from '@sentry/gatsby'

Sentry.init({
  dsn: 'https://9e8e0fab3e077e1643759be74d807cae@o253442.ingest.us.sentry.io/4507525059182592',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

export interface BaseLayoutProps extends HTMLAttributes<HTMLElement> {}

declare global {
  interface Window {
    trackGoogleAnalytics: Function
    trackGoogleAds: Function
    trackFbPixel: Function
  }
}

export const BaseLayout = ({ children }: BaseLayoutProps) => {
  const cookies = new Cookies()

  useEffect(() => {
    window.trackGoogleAnalytics && window.trackGoogleAnalytics()
    window.trackGoogleAds && window.trackGoogleAds()
    window.trackFbPixel && window.trackFbPixel()

    CookieConsent.run({
      onConsent: ({ cookie }) => {
        // consent was given
        if (typeof window !== 'undefined') {
          cookie.categories.forEach((category) => {
            switch (category) {
              case 'necessary':
                break
              case 'analytics':
                window.trackGoogleAnalytics && window.trackGoogleAnalytics()
                cookies.set('gdpr-analytics-enabled', 'true', { path: '/' })
                break
              case 'marketing':
                window.trackGoogleAds && window.trackGoogleAds()
                window.trackFbPixel && window.trackFbPixel()
                cookies.set('gdpr-marketing-enabled', 'true', { path: '/' })
                break
            }
          })
        }
      },

      onFirstConsent: ({ cookie }) => {
        if (
          cookie.categories.find(
            (category) => category === 'analytics' || category === 'marketing',
          )
        ) {
          window.location.reload()
        }
      },

      onChange: function () {
        // user changed his/her preferences
      },

      categories: {
        necessary: {
          enabled: true, // this category is enabled by default
          readOnly: true, // this category cannot be disabled
        },
        analytics: {
          enabled: false,
          readOnly: false,

          // Delete specific cookies when the user opts-out of this category
          autoClear: {
            cookies: [
              {
                name: /^_ga/, // regex: match all cookies starting with '_ga'
              },
              {
                name: '_gid', // string: exact cookie name
              },
              {
                name: 'gdpr-analytics-enabled', // regex: match all cookies starting with '_ga'
              },
            ],
          },
        },
        marketing: {
          enabled: false,
          readOnly: false,

          // Delete specific cookies when the user opts-out of this category
          autoClear: {
            cookies: [
              {
                name: /^_gcl/, // regex: match all cookies starting with '_ga'
              },
              {
                name: /^_fbp/, // regex: match all cookies starting with '_ga'
              },
              {
                name: 'gdpr-marketing-enabled', // regex: match all cookies starting with '_ga'
              },
            ],
          },
        },
      },

      language: {
        default: 'cs',

        translations: {
          cs: {
            consentModal: {
              title: 'Používáme cookies',
              // description: 'Cookie modal description',
              acceptAllBtn: 'Přijmout vše',
              acceptNecessaryBtn: 'Odmítnout vše',
              showPreferencesBtn: 'Nastavit',
            },
            preferencesModal: {
              title: 'Nastavení cookies',
              acceptAllBtn: 'Přijmout vše',
              acceptNecessaryBtn: 'Odmítnout vše',
              savePreferencesBtn: 'Přijmout aktuální nastavení',
              closeIconLabel: 'Zavřít',
              sections: [
                {
                  title: 'Povinné pro funkčnost stránek',
                  description:
                    'Soubory cookie, které jsou nutné pro funkčnost stránek a musí být povolené. Patří k nim soubory cookie, které umožňují si vás zapamatovat při procházení v rámci jedné relace.',

                  //this field will generate a toggle linked to the 'necessary' category
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Preferenční a statistické',
                  description:
                    'Tyto soubory nám pomáhají v měření používání webu, vytváření anonymních statistik a používáme je zároveň i pro zlepšení funkcí stránek pro vás. Jde například o Google Analytics.',
                  linkedCategory: 'analytics',
                },
                {
                  title: 'Marketingové',
                  description:
                    'Soubory nám pomáhají rozšiřovat povědomí o restauraci pomocí reklamy relevantním uživatelům. Zároveň nám umožňují přesnější cílení reklam a lepší vyhodnocení kampaní.',
                  linkedCategory: 'marketing',
                },
              ],
            },
          },
        },
      },
    })
  }, [])

  const handleShowPreferencesClick = () => {
    CookieConsent.showPreferences()
  }

  return (
    <ThemeProvider>
      <ThemeWrapper>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-MQ9HJQR"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          ></iframe>
        </noscript>

        <Header />

        {children}

        <Footer onCookieShowPreferencesClick={handleShowPreferencesClick} />

        <Script
          type="text/plain"
          data-category="marketing"
          dangerouslySetInnerHTML={{
            __html: `
              var leady_track_key="H2iiefHnRUUPcT6b";
              (function(){
                var l=document.createElement("script");l.type="text/javascript";l.async=true;
                l.src='https://ct.leady.com/'+leady_track_key+"/L.js";
                var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(l,s);
              })();
            `,
          }}
        />

        <Script
          type="text/plain"
          src="https://c.seznam.cz/js/rc.js"
          data-category="marketing"
        />

        <Script
          type="text/plain"
          data-category="marketing"
          dangerouslySetInnerHTML={{
            __html: `
              var conversionConf = {
                id: 100029201,
                value: 0
              };
              if (window.rc && window.rc.conversionHit) {
                window.rc.conversionHit(conversionConf);
              }
            `,
          }}
        />
      </ThemeWrapper>
    </ThemeProvider>
  )
}
